import MappingBase from './mappingBase.js';

/**
 * PaymentInfo Model
 * Represents payment tracking information for a booking
 */
class PaymentInfo extends MappingBase {
  constructor({
    paymentStatus = 0,      // 0=pending, 1=partial, 2=paid, -1=refunded
    paymentMethod = null,
    amountPaid = 0,
    depositAmount = null,
    depositPaid = false,
    balanceDue = 0,
    paymentDate = null
  } = {}) {
    super();
    this.paymentStatus = paymentStatus;
    this.paymentMethod = paymentMethod;
    this.amountPaid = amountPaid;
    this.depositAmount = depositAmount;
    this.depositPaid = depositPaid;
    this.balanceDue = balanceDue;
    this.paymentDate = paymentDate ? new Date(paymentDate) : null;
  }

  /**
   * Alias mapping for field name conversion
   * Maps backend aliases to full field names
   */
  static _aliasMapping = {
    ps: "paymentStatus",
    pm: "paymentMethod",
    ap: "amountPaid",
    da: "depositAmount",
    dp: "depositPaid",
    bd: "balanceDue",
    pd: "paymentDate"
  };

  /**
   * Payment Status Constants
   */
  static STATUS = {
    REFUNDED: -1,
    PENDING: 0,
    PARTIAL: 1,
    PAID: 2
  };

  /**
   * Check if payment is pending
   */
  get isPending() {
    return this.paymentStatus === PaymentInfo.STATUS.PENDING;
  }

  /**
   * Check if payment is partial
   */
  get isPartial() {
    return this.paymentStatus === PaymentInfo.STATUS.PARTIAL;
  }

  /**
   * Check if payment is fully paid
   */
  get isPaid() {
    return this.paymentStatus === PaymentInfo.STATUS.PAID;
  }

  /**
   * Check if payment was refunded
   */
  get isRefunded() {
    return this.paymentStatus === PaymentInfo.STATUS.REFUNDED;
  }

  /**
   * Get payment status label
   */
  get statusLabel() {
    switch (this.paymentStatus) {
      case PaymentInfo.STATUS.REFUNDED:
        return 'Refunded';
      case PaymentInfo.STATUS.PENDING:
        return 'Pending';
      case PaymentInfo.STATUS.PARTIAL:
        return 'Partially Paid';
      case PaymentInfo.STATUS.PAID:
        return 'Paid';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get formatted amount paid
   */
  get formattedAmountPaid() {
    return `$${this.amountPaid.toFixed(2)}`;
  }

  /**
   * Get formatted balance due
   */
  get formattedBalanceDue() {
    return `$${this.balanceDue.toFixed(2)}`;
  }

  /**
   * Get formatted deposit amount
   */
  get formattedDepositAmount() {
    return this.depositAmount ? `$${this.depositAmount.toFixed(2)}` : '$0.00';
  }
}

export default PaymentInfo;
